import React, { useMemo, useState } from 'react';
import { Formik, Form } from 'formik';
import ModalComponent from '../../../components/modal/modal';
import ModalBody from '../../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import FormSelect from '../../../components/dropdown/formSelect';
import InputText from '../../../components/input-text/inputText';
import CustomDatePicker from '../../../components/date-picker/customDatePicker';
import Heading from '../../../components/typography';
import type { Wealthlane_AwardSettlementService_Dtos_BrokerTradeTool_FillTradeOrderDto } from '@wealthlane/api-services/wealthlane-award-settlement-service';
import { ModalGrid } from './brokerTradeTool.styled';
import { COLORS } from '../../../constants/style/color';
import { DONE_TRADE_FILL_VALIDATION } from '../../../utils/validations/validations';
import { DONE_TRADE_FILL_INITIAL_VALUES } from '../../../constants/initialValues';
import { TransactionSingleDetailBlock } from '@/pages/transactions/transaction.styled';
import { Wealthlane_AwardService_Dtos_BrokerTradeTool_CsDoneTradePreviewDto } from '@wealthlane/api-services/wealthlane-award-service';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import AlertPopup from '../../../components/alert-popup';

type FlowMode = 'done-trade' | 'manual-fill';

interface DoneTradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  previewData: Wealthlane_AwardService_Dtos_BrokerTradeTool_CsDoneTradePreviewDto | null;
  onSubmit: (fillData: Wealthlane_AwardSettlementService_Dtos_BrokerTradeTool_FillTradeOrderDto) => void;
  isSubmitting: boolean;
  orderStatusOptions?: Array<{ label: string; value: string }>;
  isOrderStatusLoading?: boolean;
  partialFailureStep?: 'idle' | 'submitted' | 'order-created' | 'filled';
  flowMode?: FlowMode;
  /** Done Trade settings page — hide Record Type in preview */
  hideRecordType?: boolean;
  /** Done Trade settings page — lock Order Status to filled/executed */
  disableOrderStatus?: boolean;
}

const PREVIEW_FIELDS: Array<{
  label: string;
  key: keyof Wealthlane_AwardService_Dtos_BrokerTradeTool_CsDoneTradePreviewDto;
  style?: React.CSSProperties;
}> = [
    { label: 'Created Date', key: 'createdDate' },
    { label: 'Order Type', key: 'orderType' },
    { label: 'Company', key: 'company' },
    { label: 'Ticker', key: 'ticker' },
    { label: 'Shares', key: 'shares', style: { color: '#7c3aed', fontWeight: 700 } },
    { label: 'Broker Sell Type', key: 'brokerSellType' },
    { label: 'Broker Name', key: 'brokerName' },
    { label: 'Broker Account', key: 'brokerAccount' },
    { label: 'Award Type', key: 'awardType' },
    { label: 'Record Type', key: 'recordType' },
  ];

const formatPreviewValue = (key: string, value: any): string => {
  if (value == null) return '-';
  if (key === 'createdDate') return new Date(value).toLocaleDateString();
  if (key === 'orderType') return value || 'Market';
  if (key === 'brokerSellType') return value || 'Shr';
  return String(value);
};

const calculateGrossCashProceeds = (values: Record<string, any>): number =>
  parseFloat(((values.salePrice ?? 0) * (values.soldShares ?? 0)).toFixed(2));

const calculateMoneyDueParticipant = (values: Record<string, any>): number =>
  Math.max(
    0,
    parseFloat(
      (
        (values.salePrice ?? 0) * (values.soldShares ?? 0) -
        (values.fee ?? 0) -
        (values.commission ?? 0)
      ).toFixed(2)
    )
  );

const getButtonTitle = (
  isSubmitting: boolean,
  partialFailureStep: string,
  flowMode: FlowMode
): string => {
  if (isSubmitting) return 'Submitting...';
  if (partialFailureStep !== 'idle' && partialFailureStep !== 'filled') return 'Retry';
  return flowMode === 'done-trade' ? 'Submit Done Trade' : 'Submit Fill';
};

const DoneTradeModal: React.FC<DoneTradeModalProps> = ({
  isOpen,
  onClose,
  previewData,
  onSubmit,
  isSubmitting,
  orderStatusOptions,
  isOrderStatusLoading,
  partialFailureStep = 'idle',
  flowMode = 'done-trade',
  hideRecordType = false,
  disableOrderStatus = false,
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState<Record<string, any> | null>(null);

  const resolvedOrderStatusId = useMemo(() => {
    if ((!disableOrderStatus && flowMode !== 'done-trade') || !orderStatusOptions?.length) return '';
    const filled = orderStatusOptions.find(
      (o) => o.label.toLowerCase() === 'filled' || o.label.toLowerCase() === 'executed'
    );
    return filled?.value ?? '';
  }, [orderStatusOptions, flowMode, disableOrderStatus]);

  const previewFields = useMemo(
    () =>
      hideRecordType
        ? PREVIEW_FIELDS.filter((field) => field.key !== 'recordType')
        : PREVIEW_FIELDS,
    [hideRecordType]
  );

  const maxShares = previewData?.shares ?? Infinity;

  const validationSchema = useMemo(
    () => DONE_TRADE_FILL_VALIDATION(maxShares, flowMode),
    [maxShares, flowMode]
  );

  const initialValues = useMemo(
    () => DONE_TRADE_FILL_INITIAL_VALUES(previewData?.shares ?? 0, resolvedOrderStatusId),
    [previewData?.shares, resolvedOrderStatusId]
  );

  if (!previewData) return null;

  return (
    <>
      <ModalComponent
        show={isOpen}
        closeModal={onClose}
        title={flowMode === 'done-trade' ? 'Done Trade Fill Details' : 'Enter Fill Details'}
        size='xxl'
        id='doneTradeModal'>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          validateOnChange
          validateOnBlur
          onSubmit={(values) => {
            setPendingValues(values);
            setIsConfirmOpen(true);
          }}>
          {({ values, errors, touched, setFieldValue, isValid, handleSubmit }) => (
            <div>
              <ModalBody>
                <h4
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#666',
                    marginBottom: '12px',
                    marginTop: '8px',
                    textTransform: 'uppercase',
                  }}>
                  Pre-populated Details
                </h4>
                <ModalGrid>
                  {previewFields.map(({ label, key, style }) => (
                    <TransactionSingleDetailBlock key={key} id='transactionDateDetails'>
                      <div>
                        <Heading
                          variant='body-xs'
                          title={label}
                          className='detailLabel'
                          id='transactionDateLabel'
                        />
                        <Heading
                          variant='body'
                          title={formatPreviewValue(key, previewData[key])}
                          color={`${COLORS.Black}`}
                          id='transactionDateValue'
                        />
                      </div>
                    </TransactionSingleDetailBlock>
                  ))}
                  {/* <div className='field-group'>
                  <label>Order Status</label>
                  {flowMode === 'done-trade' ? (
                    <div className='field-value' style={{ color: '#047857', fontWeight: 700 }}>
                      Executed / Filled
                    </div>
                  ) : (
                    <FormSelect
                      type='text'
                      name='orderStatusId'
                      id='orderStatusSelect'
                      placeholder='Select status...'
                      options={orderStatusOptions ?? []}
                      isLoading={isOrderStatusLoading}
                      values={values.orderStatusId ?? ''}
                      arrValue={values.orderStatusId ?? ''}
                      isFormik={false}
                      onChange={(value: string) => setFieldValue('orderStatusId', value)}
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div> */}
                </ModalGrid>

                <h4
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#666',
                    marginBottom: '12px',
                    marginTop: '8px',
                    textTransform: 'uppercase',
                  }}>
                  Execution Details (Enter)
                </h4>
                <Form>
                  <ModalGrid>
                    <FormSelect
                      type='text'
                      name='orderStatusId'
                      label='Order Status'
                      id='orderStatusSelect'
                      placeholder='Select status...'
                      options={orderStatusOptions ?? []}
                      isLoading={isOrderStatusLoading}
                      values={values.orderStatusId ?? ''}
                      arrValue={values.orderStatusId ?? ''}
                      onChange={(value: string) => setFieldValue('orderStatusId', value)}
                      errors={errors}
                      touched={touched}
                      required
                      disabled={disableOrderStatus}
                    />
                    <InputText
                      label='Order Number'
                      name='orderNumber'
                      type='text'
                      placeholder='Optional'
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFieldValue('orderNumber', e.target.value)
                      }
                    />
                    <CustomDatePicker
                      label='Order Executed Date'
                      name='orderExecutedDate'
                      placeholder='Select date...'
                      setFieldValue={setFieldValue}
                      values={values}
                      required
                      onChange={() => { }}
                      errors={errors}
                      touched={touched}
                      isFormik
                      isDateFieldWithTime
                      showTimeSelect={true}
                      timeIntervals={30}
                      fixOverflow
                    />
                    <InputText
                      label='Sold Shares'
                      name='soldShares'
                      type='number'
                      required
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const val = e.target.value;
                        setFieldValue('soldShares', val === '' ? '' : parseFloat(val) || 0);
                      }}
                      errors={errors}
                      touched={touched}
                    />
                    <InputText
                      label='Fee'
                      name='fee'
                      type='number'
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const val = e.target.value;
                        setFieldValue('fee', val === '' ? '' : parseFloat(val) || 0);
                      }}
                      errors={errors}
                      touched={touched}
                    />
                    <InputText
                      label='Commission'
                      name='commission'
                      type='number'
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const val = e.target.value;
                        setFieldValue('commission', val === '' ? '' : parseFloat(val) || 0);
                      }}
                      errors={errors}
                      touched={touched}
                    />
                    <InputText
                      label='Bid Price'
                      name='bidPrice'
                      type='number'
                      placeholder='Optional'
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const val = e.target.value;
                        setFieldValue('bidPrice', val === '' ? '' : parseFloat(val) || 0);
                      }}
                    />
                    <InputText
                      label='Sale Price'
                      name='salePrice'
                      type='number'
                      required
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const val = e.target.value;
                        setFieldValue('salePrice', val === '' ? '' : parseFloat(val) || 0);
                      }}
                      errors={errors}
                      touched={touched}
                    />
                    <div className='flex justify-between items-start'>
                      <div>
                        <Heading variant='h6' title='Cash Proceeds' className='mb-8' />
                        <Heading
                          variant='h6'
                          color={COLORS.Green}
                          title={`$${calculateGrossCashProceeds(values).toFixed(2)}`}
                        />
                      </div>
                    </div>
                    <div className='flex justify-between items-start'>
                      <div>
                        <Heading variant='h6' title='Money Due Participant' className='mb-8' />
                        <Heading
                          variant='h6'
                          color={COLORS.Green}
                          title={`$${calculateMoneyDueParticipant(values).toFixed(2)}`}
                        />
                      </div>
                    </div>
                    <InputText
                      label='Trade Order Code'
                      name='tradeOrderCode'
                      type='text'
                      placeholder='Broker confirmation #'
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFieldValue('tradeOrderCode', e.target.value)
                      }
                    />
                    {/* <InputText
                      label='Counterparty ID'
                      name='counterpartyId'
                      type='text'
                      placeholder='Optional'
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFieldValue('counterpartyId', e.target.value)
                      }
                    /> */}
                  </ModalGrid>
                </Form>
              </ModalBody>
              <ModalFooterComponent
                modalStatus='Add'
                onCloseModal={onClose}
                handleSubmit={handleSubmit}
                isLoading={isSubmitting}
                addTitle=''
                buttonTitle={getButtonTitle(isSubmitting, partialFailureStep, flowMode)}
                onClickButton={handleSubmit}
                checkError={!isValid}
                id='doneTradeFooter'
              />
            </div>
          )}
        </Formik>
      </ModalComponent>

      <AnimationWrapper>
        {isConfirmOpen && (
          <AlertPopup
            isShow={isConfirmOpen}
            handleSubmit={() => {
              if (pendingValues) {
                onSubmit({
                  ...pendingValues,
                  cashProceeds: calculateMoneyDueParticipant(pendingValues),
                } as Wealthlane_AwardSettlementService_Dtos_BrokerTradeTool_FillTradeOrderDto);
              }
              setIsConfirmOpen(false);
              setPendingValues(null);
            }}
            closeModal={() => {
              setIsConfirmOpen(false);
              setPendingValues(null);
            }}
            customTitle='Are you sure you want to submit the done trade?'
            loading={isSubmitting}
            id='doneTradeConfirmModal'
          />
        )}
      </AnimationWrapper>
    </>
  );
};

export default DoneTradeModal;
